// Trust indicators pulled out of the companyDescription paragraph, where they were buried.
import { Box, Flex, Grid, Text, useColorModeValue } from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { products } from "data/products";

const Stat = ({ value, unit, label, sub }) => {
  const valueColor = useColorModeValue("#101A2E", "white");
  const unitColor = useColorModeValue("brand.500", "brand.300");
  const mutedColor = useColorModeValue("#6B7A90", "gray.400");

  return (
    <Box px={{ base: "22px", md: "28px" }} py="26px">
      <Flex align="baseline" gap="3px">
        <Text
          fontSize={{ base: "30px", md: "36px" }}
          fontWeight="700"
          lineHeight="1"
          letterSpacing="-0.045em"
          color={valueColor}
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {value}
        </Text>
        {unit && (
          <Text fontSize="16px" fontWeight="700" letterSpacing="-0.01em" color={unitColor}>
            {unit}
          </Text>
        )}
      </Flex>
      <Text
        fontFamily="mono"
        fontSize="10px"
        letterSpacing="0.13em"
        textTransform="uppercase"
        color={mutedColor}
        mt="10px"
      >
        {label}
      </Text>
      <Text fontSize="12.5px" color={mutedColor} mt="3px" lineHeight="1.5">
        {sub}
      </Text>
    </Box>
  );
};

const StatBar = () => {
  const { t } = useTranslation("home");
  // Raw value (not a theme token) because it is interpolated into an sx border shorthand.
  const lineColor = useColorModeValue("#E1E7F0", "rgba(255, 255, 255, 0.16)");
  const bg = useColorModeValue("white", "gray.700");

  const stats = [
    { key: "established", value: t("stats.established.value") },
    // Derived from the data file so it can never drift out of sync with the catalogue.
    { key: "models", value: String(products.length), unit: t("stats.models.unit") },
    { key: "delivery", value: t("stats.delivery.value"), unit: t("stats.delivery.unit") },
    { key: "rating", value: t("stats.rating.value") },
  ];

  return (
    <Grid
      templateColumns={{ base: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
      bg={bg}
      borderBottom={`1px solid ${lineColor}`}
      sx={{
        "& > * + *": { borderLeft: `1px solid ${lineColor}` },
        "@media (max-width: 959px)": {
          "& > *:nth-of-type(odd)": { borderLeft: "none" },
          "& > *:nth-of-type(n + 3)": { borderTop: `1px solid ${lineColor}` },
        },
      }}
    >
      {stats.map(({ key, value, unit }) => (
        <Stat
          key={key}
          value={value}
          unit={unit}
          label={t(`stats.${key}.label`)}
          sub={t(`stats.${key}.sub`)}
        />
      ))}
    </Grid>
  );
};

export default StatBar;
